import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Badge, Cluster, EmptyState, OptionChip, RadioGroup, Skeleton } from '../ui';
import type { RadioOption } from '../ui';
import { listInspectionTypes, inspectionTypesKeys, type InspectionType } from '../../lib/api/inspectionTypes.gen';
import { useCursorList } from '../../lib/query/useCursorList';
import { useAuth } from '../../lib/auth';
import { useCapabilities } from '../../lib/capabilities';
import { ineligibilityReason, noEligibleRemedy } from '../../lib/inspectionTypes';
import { errorMessage } from '../../lib/errorMessage';

// TypePicker (P03 §5, §7.2; story 3-3b) — "card list (name, SLA chip, template
// count, severity scale chips as OptionChips); disabled cards show role-lock
// tooltip".
//
// ── DISCOVERY, NEVER AUTHORIZATION ────────────────────────────────────────
// P03 §7.2 says it in those words. Two consequences that this file is built
// around, and neither is optional:
//
//  1. The FILTER is a convenience. The list is narrowed to the selected Site's
//     domain and, per row, marked performable-or-not from the SERVER's
//     `eligibility` — which `inspectionType.service.eligibilityResolver`
//     computes from the same permission set and membership roles that
//     `assertMayPerform` guards with. Nothing here re-derives the intersection.
//     If this component were wrong, or bypassed entirely (devtools, a stale
//     tab, a crafted request), `POST /v1/inspections` still refuses with 403
//     TYPE_ROLE. The picker cannot grant anything.
//
//  2. An INACCESSIBLE-BUT-KNOWN type is DISABLED WITH ITS REASON, not hidden.
//     Hiding it produces the worst possible support conversation ("it's not
//     there" / "it is there, for me"), and it teaches the user nothing about
//     what to ask for. The DS `RadioGroup` carries the reason as the option's
//     `description`, which is wired to the radio via `aria-describedby`, so it
//     is announced as well as visible.
//
// The list is asked for ACTIVE types only: a deactivated type is not a
// "temporarily unavailable" option, it is one that no longer applies, and
// P03 §7.2's "deactivating blocks new inspections" is exactly this. That is
// also what makes convergence observable — deactivate elsewhere and the row
// leaves this list.

export interface TypePickerProps {
  /** The selected Site's domain. `null` while no Site is chosen. */
  domainKey: string | null;
  /** That domain's record noun, from `useVocab()`. */
  domainNoun: string;
  value: string | null;
  onChange: (typeId: string | null) => void;
  disabled?: boolean;
}

export function TypePicker({ domainKey, domainNoun, value, onChange, disabled }: TypePickerProps): JSX.Element {
  const { user } = useAuth();
  const { hasPermission } = useCapabilities();

  const filters = useMemo(
    () => ({ status: 'active' as const, ...(domainKey ? { domain: domainKey } : {}) }),
    [domainKey],
  );

  const { items, isLoading, isError, error, refetch } = useCursorList<InspectionType>({
    queryKey: inspectionTypesKeys.list({ org: user?.organizationId, picker: true, ...filters }),
    fetchPage: (page) => listInspectionTypes({ ...page, ...filters }),
    limit: 50,
    enabled: Boolean(user && domainKey),
    // LIVE CONVERGENCE (story 3-3b). This list is an authorization answer, not
    // a cached page of content: a type deactivated — or a role changed — in
    // another context must stop being offered here without a page reload. The
    // app-wide defaults (`staleTime: 60s`, `refetchOnWindowFocus: false`) are
    // right for a list page and wrong for this, so the picker opts into
    // always-fresh. In the SAME session the settings mutation's
    // `invalidateQueries(inspectionTypesKeys.all)` already converges it
    // immediately; these two options extend that to a second tab/session.
    staleTime: 0,
    refetchOnWindowFocus: true,
  });

  if (!domainKey) {
    return (
      <p className="text-sm text-muted-foreground" data-testid="type-picker-no-site">
        Choose a {domainNoun.toLowerCase()} first — the procedures on offer depend on what is being inspected.
      </p>
    );
  }

  if (isLoading) return <Skeleton className="h-24 w-full" />;

  if (isError) {
    return (
      <EmptyState
        headingLevel={4}
        title="Inspection types could not be loaded"
        // G-27: the server's own sentence (this picker's sibling,
        // AssigneePicker.tsx, has always done this — a same-family bug where
        // this file hardcoded a generic message and lost whatever the server
        // actually said, e.g. a plan/permission gate on the type list itself).
        description={errorMessage(error)}
        actionLabel="Retry"
        onAction={refetch}
      />
    );
  }

  const eligible = items.filter((type) => type.eligibility.canPerform);

  // ── The no-eligible state, with the REMEDY named (P03 §7.2) ──────────────
  // "If no Type is eligible, UI distinguishes request-access vs activate-a-type
  // vs publish-a-template." The cause is resolved from the server's own reason
  // codes in lib/inspectionTypes.ts, so this is one of three (or four) distinct
  // messages with distinct links — never one generic "nothing here".
  if (eligible.length === 0) {
    const remedy = noEligibleRemedy({
      domainNoun,
      types: items,
      canManageTypes: hasPermission('types:manage'),
      canManageTemplates: hasPermission('templates:manage'),
    });
    return (
      <div data-testid="type-picker-empty" data-remedy={remedy.kind} className="space-y-3">
        <EmptyState headingLevel={4} title={remedy.title} description={remedy.description} />
        {remedy.action && (
          <p className="text-center text-sm">
            <Link to={remedy.action.to} className="focus-ring font-semibold text-primary underline">
              {remedy.action.label}
            </Link>
          </p>
        )}
        {/* Even with nothing selectable, the types that EXIST stay visible with
            their reason — the same "disabled, not hidden" rule as above. */}
        {items.length > 0 && (
          <ul className="space-y-1 text-xs text-muted-foreground">
            {items.map((type) => (
              <li key={type._id} data-testid="type-picker-blocked">
                <span className="font-semibold text-foreground">{type.name}</span> —{' '}
                {ineligibilityReason(type.eligibility)}
              </li>
            ))}
          </ul>
        )}
      </div>
    );
  }

  const options: RadioOption[] = items.map((type) => ({
    value: type._id,
    label: type.name,
    disabled: disabled || !type.eligibility.canPerform,
    ...(type.eligibility.canPerform ? {} : { description: ineligibilityReason(type.eligibility) }),
    content: (
      <Cluster as="span" gap={1} className="mt-1">
        {type.slaHours !== null && <Badge tone="neutral">{`${type.slaHours} h SLA`}</Badge>}
        <Badge tone="neutral">
          {type.templateIds.length} template{type.templateIds.length === 1 ? '' : 's'}
        </Badge>
        {type.severityScale.map((level) => (
          <OptionChip key={level.key} small option={{ label: level.label, color: level.color }} />
        ))}
      </Cluster>
    ),
  }));

  return (
    <RadioGroup
      name="inspectionTypeId"
      id="inspection-type-picker"
      value={value ?? undefined}
      onChange={(event) => onChange(event.target.value)}
      options={options}
      {...(disabled ? { disabled: true } : {})}
    />
  );
}
